// Live-share consent control (#291). Two SEPARATE toggles — the master "share my
// live data" (default OFF) and a "include my inputs" carve-out (throttle/brake/
// steering = competitive data; only while sharing is on). Wired to GET/PUT
// /api/live/consent. Distinct from the post-session leaderboard's share_telemetry.
import { useEffect, useState } from "react";
import { api } from "../utils/api";

interface Props {
  teamId: string;
  teamName?: string;
}

interface Consent {
  enabled: boolean;
  shareInputs: boolean;
}

function Switch({ on, disabled, onClick, label }: {
  on: boolean; disabled?: boolean; onClick: () => void; label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 40, height: 22, borderRadius: 11, padding: 0,
        border: "1px solid var(--rule-strong)",
        background: on ? "var(--live)" : "var(--ink-3)",
        position: "relative", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, flexShrink: 0, transition: "background 0.15s",
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: on ? 20 : 2, width: 16, height: 16,
        borderRadius: "50%", background: "var(--text-strong)", transition: "left 0.15s",
      }} />
    </button>
  );
}

export default function LiveShareToggle({ teamId, teamName }: Props) {
  const [enabled, setEnabled] = useState(false);
  const [shareInputs, setShareInputs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api<Consent>(`/api/live/consent?teamId=${teamId}`)
      .then((c) => { if (alive) { setEnabled(c.enabled); setShareInputs(c.shareInputs); } })
      .catch(() => { /* not a member / offline — leave OFF */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [teamId]);

  // PESSIMISTIC save (consent control): the toggle reflects ONLY the server's
  // confirmed state — never an optimistic flip. So a failed PUT leaves the switch
  // where it was (the safe direction: it can't read "off" while the server still
  // has you "on"). On success we take the server echo as truth.
  async function save(next: Consent) {
    setSaving(true);
    try {
      const res = await api<Consent>(`/api/live/consent`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ teamId, enabled: next.enabled, shareInputs: next.shareInputs }),
      });
      setEnabled(res.enabled); setShareInputs(res.shareInputs);
    } catch {
      // Re-sync to the server's truth (best effort); the UI never flipped optimistically.
      try {
        const c = await api<Consent>(`/api/live/consent?teamId=${teamId}`);
        setEnabled(c.enabled); setShareInputs(c.shareInputs);
      } catch { /* ignore — switch stays at its last confirmed state */ }
    } finally {
      setSaving(false);
    }
  }

  const rowStyle: React.CSSProperties = {
    display: "flex", alignItems: "flex-start", gap: 12, justifyContent: "space-between",
  };
  const labelStyle: React.CSSProperties = { fontFamily: "var(--font-body)", fontSize: 13 };
  // --text-secondary (not --text-muted): a privacy disclosure must clear the AA
  // contrast bar on --ink-2.
  const subStyle: React.CSSProperties = {
    fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)",
    marginTop: 2, lineHeight: 1.4, maxWidth: 360,
  };

  return (
    <div style={{
      background: "var(--ink-2)", border: "1px solid var(--rule)", borderRadius: 10,
      padding: 16, opacity: loading ? 0.6 : 1,
    }}>
      <div style={{
        fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600,
        color: "var(--text-strong)", marginBottom: 12,
      }}>
        Live sharing
      </div>

      <div style={rowStyle}>
        <div>
          <div style={{ ...labelStyle, color: "var(--text-default)" }}>
            Share my live data with {teamName || "this team"}
          </div>
          <div style={subStyle}>
            Teammates can see your live timing, fuel, and position while you run. Off by default — switch it off whenever you want.
          </div>
        </div>
        <Switch
          on={enabled} disabled={saving} label="Share live data"
          onClick={() => save({ enabled: !enabled, shareInputs })}
        />
      </div>

      <div style={{ height: 1, background: "var(--rule)", margin: "14px 0" }} />

      <div style={rowStyle}>
        <div>
          <div style={{ ...labelStyle, color: enabled ? "var(--text-default)" : "var(--text-muted)" }}>
            Include my inputs
          </div>
          <div style={subStyle}>
            Throttle, brake, and steering — competitive data, so it's a separate opt-in and only shares while live sharing is on.
          </div>
        </div>
        <Switch
          on={shareInputs} disabled={!enabled || saving} label="Include inputs"
          onClick={() => {
            if (!enabled) return;
            save({ enabled, shareInputs: !shareInputs });
          }}
        />
      </div>
    </div>
  );
}
